import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  ButtonBase,
  Box,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { ReactNode } from "react";

type Variant = "primary" | "default" | "destructive";

const TITLE_COLOR_BY_VARIANT: Record<Variant, string> = {
  primary: "primary.main",
  default: "text.primary",
  destructive: "error.main",
};

type Props = {
  title: string;
  icon?: ReactNode;
  subtitle?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  variant?: Variant;
};

export const ActionRow = ({
  icon,
  title,
  subtitle,
  onClick,
  isDisabled,
  variant = "default",
}: Props) => {
  const { palette } = useTheme();

  const isInteractive = !isDisabled && !!onClick;

  const accentColorByVariant: Record<Variant, string> = {
    primary: palette.primary.main,
    default: palette.text.secondary,
    destructive: palette.error.main,
  };

  const accentColor = accentColorByVariant[variant];
  const titleColor = TITLE_COLOR_BY_VARIANT[variant];

  return (
    <ButtonBase
      onClick={isInteractive ? onClick : undefined}
      disabled={!isInteractive}
      focusRipple
      sx={{
        width: "100%",
        px: 4,
        py: { xs: 3, sm: 4.5 },
        justifyContent: "flex-start",
        textAlign: "left",
        borderRadius: 0,
        opacity: isDisabled ? 0.5 : 1,
        cursor: isInteractive ? "pointer" : "default",
        "&:hover": isInteractive
          ? { backgroundColor: palette.action.hover }
          : undefined,
      }}
    >
      <Box display="flex" alignItems="center" gap={2} width="100%">
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha(accentColor, 0.1),
              color: accentColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Stack flex={1} gap={0.25} minWidth={0}>
          <Typography fontSize={16} fontWeight={500} color={titleColor}>
            {title}
          </Typography>
          {subtitle && (
            <Typography fontSize="0.875rem" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>
        {isInteractive && (
          <ChevronRightIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
        )}
      </Box>
    </ButtonBase>
  );
};
